/**
 * Created by staffanrantzow on 05/04/16.
 */

import axios from 'axios';
import InvoiceEntity from './invoice-entity';
import InvoiceCalculationEntity from './invoice-calculation-entity';
import CustomerEntity from '../customers/customer-entity';
import AddressEntity from '../address/address-entity';
import {APIResult} from '../http';
import Service from '../service/service';
import Utils from '../utils/utils';
import CurrencyService from '../currencies/currencies-service';
import InvoicePostEntity from './invoice-post-entity';
import InvoiceDateEntity from './invoice-date-entity';
import InvoiceHsdEntity from './invoice-hsd-entity';
import InvoiceUserService from './invoice-user-service';
import SalaryService from 'frilans-finans/salaries/salary-service';
import { SalaryPaymentStatusConstants } from 'frilans-finans';
import SupplierLedgerService from '../supplier-ledgers/supplier-ledger-service';
import PendingRequestEntity from '../entities/pending-request-entity';
import moment from 'moment';

class InvoiceService extends Service {

  constructor () {
    super();
    this.requests = [];
  }

  /**
   *
   * @param data the invoices
   * @param included included data for each invoice (include=invoicesdates)
   * @returns {Array}
   */
  hydrateCollection (data, included) {
    var invoices = [];

    for (let i = 0; i < data.length; i++) {
      invoices.push(this.hydrate(data[i], included));
    }

    return invoices;
  }

  hydrateCustomer (invoice) {
    var attr = invoice.attributes;

    return new CustomerEntity(
      attr.company_id,
      attr.company_contact,
      attr.company_email,
      attr.company_name,
      attr.company_phone,
      attr.send_to_email,
      attr.send_to_email_secondary,
      attr.user_id,
      attr.company_vat,
      new AddressEntity(
        attr.company_city,
        attr.company_co,
        attr.company_country,
        attr.company_street,
        attr.company_zip
      )
    );
  }

  hydrate (invoice, included = []) {
    let attr = invoice.attributes;
    let currencyEntity = null;
    let invoiceUsers = [];
    let invoiceDates = [];
    let invoiceHours = 0;
    let pdfLink = null;
    let salaries = [];
    let calculation = {};
    let users = {};
    let loyaltyPoints = 0;
    let hsd = null;

    // Loop through the included data and store it correctly
    for (let inc of included) {
      if (inc.type === 'invoice-users' && parseInt(inc.attributes.invoice_id) === parseInt(invoice.id)) {
        invoiceUsers.push(InvoiceUserService.hydrate(inc));
      }
      if (inc.type === 'invoice-dates' && parseInt(inc.attributes.invoice_id) === parseInt(invoice.id)) {
        invoiceDates.push(this.hydrateInvoiceDates(inc));
        invoiceHours += inc.attributes.hours;
      }
      if (inc.type === 'currencies') {
        currencyEntity = CurrencyService.hydrate(inc);
      }

      if (inc.type === 'pdf' && ((((invoice.relationships || {}).pdf || {}).data || {}).id || '') === inc.id) {
        pdfLink = inc.attributes.url;
      }

      if (inc.type === 'salaries' && parseInt(inc.attributes.invoice_id) === parseInt(invoice.id)) {
        let salaryEntity = SalaryService.hydrate(inc, '');

        if (salaryEntity.isRegular()) {
          salaryEntity.taxAgencySalary = this.getTaxAgencySalary(included, salaryEntity);
        }

        if (salaryEntity.paymentStatus !== SalaryPaymentStatusConstants.NOT_APPROVED_STATUS) {
          salaries.push(salaryEntity);
        }
      }

      if (inc.type === 'calculation' && inc.id === invoice.relationships.calculation.data.id) {
        calculation = inc.attributes;
      }
      if (inc.type === 'users' && inc.id === invoice.relationships.users.data[0].id) {
        users = inc.attributes;
      }

      if (inc.type === 'loyalty-points' &&
        invoice.relationships.loyaltypoints.data.length &&
        inc.id === invoice.relationships.loyaltypoints.data[0].id
      ) {
        loyaltyPoints = +inc.attributes.amount * +inc.attributes.factor;
      }

      if (inc.type === 'hsds') {
        hsd = inc.attributes;
      }
    }

    return new InvoiceEntity(
      invoice.id,
      attr.allowance,
      attr.amount,
      attr.approval_status,
      attr.created_at,
      attr.currency_amount,
      attr.currency_name,
      attr.currency_exchange_rate,
      attr.currency_id,
      attr.expense,
      attr.full_payment_date_time,
      attr.gross,
      attr.has_credit_errand,
      attr.invoice_number,
      loyaltyPoints,
      attr.net,
      attr.payment_status,
      attr.pre_report,
      attr.pre_reported_at,
      attr.send_to_email,
      attr.sent,
      attr.specification,
      attr.status,
      attr.supplier_ledger_date_time,
      attr.tax_id,
      attr.tax_percent,
      attr.travel,
      attr.user_id,
      this.hydrateCustomer(invoice),
      currencyEntity,
      invoiceHours,
      invoiceUsers,
      pdfLink,
      hsd,
      attr.display_dates,
      attr.display_hours,
      salaries,
      invoiceDates,
      calculation,
      attr.ocr,
      attr.uuid,
      users,
      attr.message_type,
      attr.emails,
      attr.e_invoice_address,
      attr.po_number,
      attr.company_phone
    );
  }

  getTaxAgencySalary (included, salary) {
    let taxAgencySalaries = included.filter(inc => {
      return inc.type === 'tax-agency-salaries' &&
        salary.userId === inc.attributes.user_id;
    });

    if (taxAgencySalaries) {
      return taxAgencySalaries[0];
    }

    return null;
  }

  hydrateInvoiceCalculation (data, included) {
    let invoiceCalculation = {};
    invoiceCalculation.amount = data.attributes.amount;
    invoiceCalculation.amountIncludingVat = data.attributes.amount_including_vat;
    invoiceCalculation.amountAfterHsd = data.attributes.amount_after_household_service_deduction;
    invoiceCalculation.invoiceCalculations = [];
    if (included !== undefined) {
      for (let i = 0; i<included.length; i++) {
        let a = included[i].attributes;
        switch (included[i].type) {
          case 'invoice-calculation-workers':
            invoiceCalculation.invoiceCalculations.push(
              new InvoiceCalculationEntity(
                a.allowance,
                a.amount,
                a.expense,
                a.fee,
                a.gross,
                a.invoice_value,
                a.itp,
                a.itp_tax,
                a.net,
                a.payroll_tax,
                a.salary_total,
                a.tax,
                a.to_pay,
                a.total_payroll_tax,
                a.travel,
                a.vacation_gross,
                a.vacation_net,
                a.vacation_payroll_tax,
                data.attributes.vat,
                included[i].id
              )
            );
            break;
        }
      }
    }

    return invoiceCalculation;
  }

  hydrateInvoiceDatesCollection (data) {
    let invoiceDates = [];
    for (let i = 0; i < data.length; i++) {
      invoiceDates.push(this.hydrateInvoiceDates(data[i]));
    }
    return invoiceDates;
  }

  hydrateInvoiceDates (data) {
    data = data.attributes;
    return new InvoiceDateEntity(
      data.date,
      data.hours,
      data.invoice_id
    );
  }

  hydrateHsd (data) {
    if (data) {
      data = data.attributes;
      return new InvoiceHsdEntity(
        data.rot,
        data.rut,
        data.data,
        data.apartment_number,
        data.cooperative_apartment_registration_number,
        data.accommodation_type
      );
    }

    return {};
  }

  hydrateInvoiceWithUsers (data, included) {
    data = data.attributes;
    let usersData = this.parseIncludedData(included, 'invoice-users');
    let datesData = this.parseIncludedData(included, 'invoice-dates');
    let hsdData = this.parseIncludedData(included, 'hsds');
    return new InvoicePostEntity(
      data.specification,
      data.user_id,
      data.tax_id,
      data.company_id,
      data.currency_id,
      data.currency_name,
      data.amount,
      InvoiceUserService.hydrateCollection(usersData, data.user_id),
      this.hydrateInvoiceDatesCollection(datesData),
      data.pre_report,
      data.pre_reported_at,
      data.created_at,
      this.hydrateHsd(hsdData[0]),
      data.send_to_email,
      data.e_invoice_address ? data.e_invoice_address : '',
      data.display_dates,
      data.display_hours,
      data.coupon_id,
      data.currency_amount,
      data.message_type
    );
  }

  lockInvoice (invoiceId) {
    return axios.post(`backend://invoices/${invoiceId}/lock`, {})
    .then((response) => {
      return response;
    });
  }

  post (params) {
    let data = {
      data: {
        attributes: params
      }
    };
    return axios.request({
      url: 'backend://invoices',
      method: 'POST',
      data: data
    });
  }

  update (params, invoiceId) {
    let data = {
      data: {
        attributes: params
      }
    };
    return axios.request({
      url: 'backend://invoices/' + invoiceId,
      method: 'PATCH',
      data: data
    });
  }

  delete (id) {
    return axios.delete('backend://invoices/' + id);
  }

  parseIncludedData (data, type) {
    let parsedData = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].type === type) {
        parsedData.push(data[i]);
      }
    }
    return parsedData;
  }

  getPreReportFilter (preReport) {
    if (Utils.isInt(preReport)) {
      return '&filter[pre_report]=' + preReport;
    }

    return '';
  }

  getActiveFilter (active) {
    if (active) {
      return '&filter[active]=' + active;
    }

    return '';
  }

  getCurrentFilter (current) {
    if (current) {
      return '&filter[current]=1';
    }

    return '';
  }

  getPaidFilter (paid) {
    if (paid === true) {
      return '&filter[paid]=1';
    }

    if (paid === false) {
      return '&filter[unPaidOrPartiallyPaid]=1';
    }

    return '';
  }

  getSupplierLedgersForInvoice (invoiceId) {
    return axios.get(`backend://invoices/${invoiceId}/supplier-ledgers?include=appendixfiles`, {
      params: {
        'page[size]': 100
      }
    })
    .then((response) => SupplierLedgerService.hydrateCollection(response.data.data, response.data.included));
  }

  getById (id) {
    return axios.get('backend://invoices/' + id +
      '?include=invoicedates,invoiceusers,currency,pdf,hsd,salaries,taxagencysalaries,loyaltypoints')
      .then((response) => this.hydrate(response.data.data, response.data.included));
  }

  getByUuid (uuid, includes = []) {
    return axios.get(`backend://invoices/uuid/${uuid}?include=${includes.join(',')}`)
      .then((response) => this.hydrate(response.data.data, response.data.included));
  }

  get (userId, dateFilter, page, preReport, active, size, paid, current) {
    // if we look for preReported invoices we don't care about date period
    const filter = preReport ? '?' : this.getFilterDate(dateFilter, 'created_at');
    const preReportFilter = this.getPreReportFilter(preReport);
    const activeFilter = this.getActiveFilter(active);
    const paidFilter = this.getPaidFilter(paid);
    const currentFilter = this.getCurrentFilter(current);

    let pendingRequest = new PendingRequestEntity();

    pendingRequest.promise = axios.get('backend://users/' + userId + '/invoices' +
      filter +
      preReportFilter +
      activeFilter +
      currentFilter +
      paidFilter +
      '&sort=-created_at', {
        params: {
          'page[number]': page || 1,
          'page[size]': size || 3,
          'include': 'invoicedates,currency,pdf,loyaltypoints,salaries'
        },
        cancelToken: new axios.CancelToken(function executor (c) {
          pendingRequest.cancel = c;
        })
      })
      .then((response) => {
        var invoices = this.hydrateCollection(response.data.data, response.data.included);
        return new APIResult(invoices, response.data.meta);
      });

    return pendingRequest;
  }

  getForEmployerCertificates (dateFilter, page, size) {
    let filter = this.getFilterDate(dateFilter, 'created_at');
    let pendingRequest = new PendingRequestEntity();
    pendingRequest.promise = axios.get(
      'backend://invoices' + filter + '&filter[eligibleForEmploymentCertificate]=true' +
        '&sort=-created_at', {
          params: {
            'page[number]': page || 1,
            'page[size]': size || 3,
            'include': 'invoicedates,currency,invoiceusers,salaries'
          },
          cancelToken: new axios.CancelToken(function executor (c) {
            pendingRequest.cancel = c;
          })
        })
    .then((response) => {
      var invoices = this.hydrateCollection(response.data.data, response.data.included);
      return new APIResult(invoices, response.data.meta);
    });

    return pendingRequest;
  }

  getInvoiceByIdIncludeUsersDates (id) {
    return axios.get('backend://invoices/' + id, {
      params: {
        'include': 'invoiceusers,invoicedates,hsd'
      }
    })
    .then((response) => {
      return this.hydrateInvoiceWithUsers(response.data.data, response.data.included);
    });
  }

  getLastTwelveMonths () {
    let startMonth = new Date().getMonth() + 2;
    let startYear = new Date().getFullYear() - 1;

    let start = moment(startYear + '-' + startMonth + '-01');
    let end = moment().format();

    let filter = '?filter[created_at][>][]=' + Utils.formatDate(start) +
      '&filter[created_at][<][]=' + Utils.formatDate(end);

    let pendingRequest = new PendingRequestEntity();

    pendingRequest.promise = axios.get('backend://invoices' + filter + '&include=invoicedates,currency', {
      params: {
        'page[number]': 1,
        'page[size]': 1000
      },
      cancelToken: new axios.CancelToken(function executor (c) {
        pendingRequest.cancel = c;
      })
    })
      .then((response) => {
        var invoices = this.hydrateCollection(response.data.data, response.data.included);
        return new APIResult(invoices, response.data.meta);
      });

    return pendingRequest;
  }

  getInvoiceCalculation (calculatorPostEntity) {
    this.requests.push(calculatorPostEntity);
    let numRequests = this.requests.length;
    return axios.post('backend://invoicecalculations', {
      data: {
        attributes: calculatorPostEntity
      }
    })
      .then((response) => {
        if (numRequests === this.requests.length) {
          return this.hydrateInvoiceCalculation(response.data.data, response.data.included);
        } else {
          console.log('Ignored invoice calculation request');
        }
      });
  }

  unlockInvoice (invoiceId) {
    return axios.delete(`backend://invoices/${invoiceId}/lock`);
  }
}

export default new InvoiceService();



/** WEBPACK FOOTER **
 ** ./src/frilans-finans/invoices/invoice-service.js
 **/